<template>
  <b-modal ref="modal" no-fade v-model="dialog.show" size="xl" :id="`modal-organization-${uniqueId}`"
           hide-footer no-close-on-backdrop scrollable
           @hide="onHide" @hidden="onHidden"
  >
    <template #modal-title>
      <div v-if="isNewOrganization" class="d-flex align-items-center">
        <i class="fa-solid fa-building-circle-check text-primary mr-50"></i> New organization
      </div>
      <div v-else-if="!loading && error == null" class="d-flex align-items-center">
        <div class="org-avatar mr-1" :style="avatarStyle">{{ (organization.acronym || '?').substring(0, 3).toUpperCase() }}</div>
        <div>
          <div class="d-flex align-items-center">
            {{ organization.name }}
            <b-badge v-if="organization.desk" variant="light-secondary" class="ml-50">{{ organization.desk }}</b-badge>
            <b-badge v-if="dirty" variant="light-warning" class="ml-25">Unsaved</b-badge>
          </div>
          <small class="text-muted">{{ organization.id }}</small>
        </div>
      </div>
      <span v-else>Organization</span>
    </template>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary"/>
    </div>
    <div v-else-if="error != null" class="py-5 text-center">
      <b-alert show variant="danger" class="d-inline-block px-2">
        <i class="fa-sharp fa-solid fa-triangle-exclamation"></i> {{ error }}
      </b-alert>
      <div><b-button size="sm" variant="outline-primary" @click="refreshData">Retry</b-button></div>
    </div>
    <organization-edit-details v-else v-model="organization" :new-organization="isNewOrganization"
                               @dirty="val => dirty = val" @saved="onSaved" @cancel="dialog.show = false"/>
  </b-modal>
</template>

<script>
import mixinBase from '@/components/mixin/mixinBase';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import { mapActions, mapGetters } from 'vuex'
import OrganizationEditDetails from '@/components/partial/organizations/OrganizationEditDetails';
import Organization from '@/model/organization/Organization'

export default {
  name: 'OrganizationModal',
  components: { OrganizationEditDetails },
  mixins: [mixinBase, mixinNotifications],
  data: () => ({
    dialog: {
      show: false,
    },
    uniqueId: 0,
    loading: true,
    error: null,
    id: null,
    isNewOrganization: false,
    organization: {},
    dirty: false,
    saved: false,
  }),
  created() {
    this.uniqueId = this._uid
  },
  mounted() {
    // Deep link: /configurations/organizations?organizationId=...
    const { organizationId } = this.queryParams()
    if (organizationId) {
      this.showModal(organizationId)
    }
  },
  computed: {
    ...mapGetters('data', ['platformId']),
    avatarStyle() {
      const color = this.organization.color
      if (!/^#[0-9a-f]{6}$/i.test(color || '')) {
        return { backgroundColor: '#e9ecef', color: '#6e6b7b' }
      }
      const r = parseInt(color.substr(1, 2), 16), g = parseInt(color.substr(3, 2), 16), b = parseInt(color.substr(5, 2), 16)
      return { backgroundColor: color, color: (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6 ? '#222' : '#fff' }
    },
  },
  methods: {
    ...mapActions('organizations', ['getOrganizationById']),
    showModal(id) {
      this.id = id;
      this.error = null
      this.dirty = false
      this.saved = false
      this.dialog.show = true
      this.isNewOrganization = this.id == null || this.id === 0;
      if (this.isNewOrganization) {
        this.organization = Organization({ lang: "en", platformId: this.platformId });
        this.loading = false
      } else {
        this.refreshData()
        this.pushState({ organizationId: id })
      }
    },
    closeModal() {
      this.dialog.show = false;
    },
    onSaved(organization) {
      this.organization = organization
      this.saved = true
      this.dirty = false
      this.dialog.show = false
    },
    onHide(event) {
      if (!this.dirty || this.saved) {
        return
      }
      event.preventDefault()
      this.$bvModal.msgBoxConfirm('You have unsaved changes. Close and discard them?', {
        title: this.$t('globalMessages.pleaseConfirm'),
        size: 'sm',
        okVariant: 'danger',
        okTitle: 'Discard changes',
        cancelTitle: this.$t('operations.cancel'),
        centered: true,
      }).then(confirmed => {
        if (confirmed) {
          this.dirty = false
          this.dialog.show = false
        }
      })
    },
    onHidden() {
      this.id = null;
      this.organization = {}
      this.loading = true
      this.pushState({ organizationId: null, tab: null })
      this.$emit('closed', { saved: this.saved })
    },
    refreshData() {
      this.loading = true
      this.error = null
      this.getOrganizationById(this.id)
        .then(organization => {
          this.organization = organization;
        }, err => {
          this.error = this.getErrorDescription(err) || 'Failed to load organization'
        })
        .finally(() => { this.loading = false })
    },
  }
};
</script>

<style scoped>
.org-avatar {
  width: 38px;
  height: 38px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: .8rem;
}
</style>
